import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import to from 'await-to-js'
import Checkbox from 'components/Checkbox/checkbox'
import FbButton from 'components/FbUI/FbButton'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { cn } from 'components/UI/cn'
import { EmailSignature } from 'models/emailIntegration'
import DOMPurify from 'dompurify'
import { Pencil, Trash2 } from 'lucide-react'
import dialogConfirm from 'components/dialogConfirm'
import { OverflownText } from 'components/OverflownText'

const SIGNATURES_KEY = ['email-signatures']

// KEEP IN SYNC with the backend constant
// MAX_SIGNATURE_HTML_BYTES in
// server/email_integration/services/signature_sanitizer.py
const MAX_HTML_BYTES = 8 * 1024
const htmlByteLength = (s: string): number => new TextEncoder().encode(s).length

export function EmailIntegrationSignatures() {
  const api = useApiService()
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)

  const { data, isFetching } = useQuery({
    queryKey: SIGNATURES_KEY,
    queryFn: async ({ signal }) => {
      const [err, res] = await to(
        api.emailIntegration.getEmailSignatureList(signal)
      )
      if (err) throw err
      return res
    },
  })

  const signatures = data?.results ?? []

  const handleSaved = () => {
    setAdding(false)
    setEditingId(null)
    queryClient.invalidateQueries({ queryKey: SIGNATURES_KEY })
  }

  const descriptionText =
    'Add and manage your signatures. You can have multiple — set one as the default.'

  return (
    <div className="flex flex-col gap-4 w-full overflow-y-auto">
      <div className="flex items-start justify-between">
        <OverflownText
          tooltipText={descriptionText}
          maxLines={4}
          placement="bottom"
          className="text-[#666]"
        >
          {descriptionText}
        </OverflownText>

        {!adding && (
          <FbButton
            onClick={() => {
              setAdding(true)
              setEditingId(null)
            }}
          >
            Add signature
          </FbButton>
        )}
      </div>

      {adding && (
        <SignatureEditor
          onCancel={() => setAdding(false)}
          onSaved={handleSaved}
        />
      )}

      {isFetching && signatures.length === 0 ? (
        <div className="text-sm text-fg-secondary">Loading signatures…</div>
      ) : signatures.length === 0 && !adding ? (
        <div className="rounded-md border border-line bg-surface p-4 text-sm text-fg-secondary italic">
          You don&apos;t have any signatures yet.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {signatures.map((sig) =>
            editingId === sig.id ? (
              <SignatureEditor
                key={sig.id}
                signature={sig}
                onCancel={() => setEditingId(null)}
                onSaved={handleSaved}
              />
            ) : (
              <SignatureItem
                key={sig.id}
                signature={sig}
                onEdit={() => setEditingId(sig.id)}
              />
            )
          )}
        </div>
      )}
    </div>
  )
}

function SignatureItem({
  signature,
  onEdit,
}: {
  signature: EmailSignature
  onEdit: () => void
}) {
  const api = useApiService()
  const queryClient = useQueryClient()
  const sanitized = useMemo(
    () => DOMPurify.sanitize(signature.html),
    [signature.html]
  )

  const setDefaultMutation = useMutation({
    mutationFn: async () => {
      const [err] = await to(
        api.emailIntegration.setDefaultEmailSignature(signature.id)
      )
      if (err) throw err
    },
    onSuccess: () => {
      toast.success('Set as default')
      queryClient.invalidateQueries({ queryKey: SIGNATURES_KEY })
    },
    onError: () => toast.error('Failed to set default'),
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const [err] = await to(
        api.emailIntegration.deleteEmailSignature(signature.id)
      )
      if (err) throw err
    },
    onSuccess: () => {
      toast.success('Signature deleted')
      queryClient.invalidateQueries({ queryKey: SIGNATURES_KEY })
    },
    onError: () => toast.error('Failed to delete'),
  })

  const handleDelete = async () => {
    const ok = await dialogConfirm(
      signature.is_default
        ? 'This is the default signature. Another will be promoted automatically.'
        : 'This signature will be removed.',
      'Delete signature?',
      '',
      { confirmLabel: 'Delete', buttonVariant: 'danger' }
    )
    if (ok) deleteMutation.mutate()
  }

  return (
    <div className="rounded-md border border-line bg-surface p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-fg truncate">
            {signature.name || 'Untitled'}
          </span>
          {signature.is_default && (
            <span className="text-xxs px-2 py-0.5 rounded-full bg-brand/10 text-brand">
              Default
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!signature.is_default && (
            <button
              type="button"
              onClick={() => setDefaultMutation.mutate()}
              disabled={setDefaultMutation.isPending}
              className="text-xs text-brand hover:underline px-2 py-1 disabled:opacity-50"
              title="Set as default"
            >
              Set default
            </button>
          )}
          <button
            type="button"
            onClick={onEdit}
            className="p-1 rounded hover:bg-surface-hover text-fg-secondary hover:text-fg"
            title="Edit"
          >
            <Pencil className="size-4" />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="p-1 rounded hover:bg-surface-hover text-fg-secondary hover:text-red-600 disabled:opacity-50"
            title="Delete"
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      </div>

      <div
        className="border border-line rounded p-3 bg-surface-hover text-sm overflow-auto"
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    </div>
  )
}

function SignatureEditor({
  signature,
  onCancel,
  onSaved,
}: {
  signature?: EmailSignature
  onCancel: () => void
  onSaved: () => void
}) {
  const api = useApiService()
  const isEditing = !!signature

  const [name, setName] = useState(signature?.name ?? '')
  const [html, setHtml] = useState(signature?.html ?? '')
  const [isDefault, setIsDefault] = useState(signature?.is_default ?? false)
  const sanitizedPreview = useMemo(() => DOMPurify.sanitize(html), [html])
  const htmlBytes = useMemo(() => htmlByteLength(html), [html])
  const isOverByteLimit = htmlBytes > MAX_HTML_BYTES

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isEditing && signature) {
        const [err] = await to(
          api.emailIntegration.updateEmailSignature(signature.id, {
            name,
            html,
            ...(isDefault && !signature.is_default ? { is_default: true } : {}),
          })
        )
        if (err) throw err
      } else {
        const [err] = await to(
          api.emailIntegration.createEmailSignature({
            name,
            html,
            is_default: isDefault,
          })
        )
        if (err) throw err
      }
    },
    onSuccess: () => {
      toast.success(isEditing ? 'Signature saved' : 'Signature created')
      onSaved()
    },
    onError: () => toast.error('Failed to save signature'),
  })

  return (
    <div className="rounded-md border border-line bg-surface p-4 flex flex-col gap-3">
      <div className="text-sm font-medium text-fg">
        {isEditing ? 'Edit signature' : 'New signature'}
      </div>
      <label className="flex flex-col gap-1 text-xs text-fg-secondary">
        Label
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Default, Outreach, Internal"
          className={cn(
            'w-full rounded border border-line bg-surface px-3 py-2 text-sm text-fg',
            'focus:outline-none focus:border-brand'
          )}
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-fg-secondary">
        Signature HTML
        <span className="text-fg-muted font-normal">
          Copy your signature from Gmail or Outlook (right-click → Inspect →
          copy the signature element&apos;s outer HTML), or write your own.
          Plain text works too.
        </span>
        <textarea
          value={html}
          onChange={(e) => setHtml(e.target.value)}
          rows={6}
          className={cn(
            'w-full rounded border bg-surface px-3 py-2 text-sm font-mono text-fg',
            'focus:outline-none',
            isOverByteLimit
              ? 'border-red-500 focus:border-red-500'
              : 'border-line focus:border-brand'
          )}
          placeholder={`<p>Sarah Chen</p>\n<p>Senior Account Executive · Acme</p>\n<p><a href="mailto:sarah@acme.com">sarah@acme.com</a></p>`}
        />
      </label>
      <div
        className={cn(
          'flex items-center justify-between text-xxs',
          isOverByteLimit ? 'text-red-600' : 'text-fg-secondary'
        )}
      >
        <span>
          {isOverByteLimit
            ? `Signature HTML exceeds the ${MAX_HTML_BYTES / 1024} KB limit. Trim it before saving.`
            : ''}
        </span>
        <span aria-live="polite">
          {(htmlBytes / 1024).toFixed(1)} / {MAX_HTML_BYTES / 1024} KB
        </span>
      </div>
      {html.trim() !== '' && (
        <div
          className="border border-line rounded p-3 bg-surface-hover text-sm overflow-auto"
          dangerouslySetInnerHTML={{ __html: sanitizedPreview }}
        />
      )}

      <div className="flex items-center justify-between gap-3 pt-1">
        <label className="flex items-center gap-2 text-xs text-fg-secondary cursor-pointer">
          <Checkbox
            checked={isDefault}
            disabled={signature?.is_default}
            onChange={(e) => setIsDefault(e.currentTarget.checked)}
          />
          <span>Set as default</span>
        </label>
        <div className="flex items-center gap-2">
          <FbButton
            variant="secondary"
            onClick={onCancel}
            disabled={saveMutation.isPending}
          >
            Cancel
          </FbButton>
          <FbButton
            onClick={() => saveMutation.mutate()}
            disabled={
              saveMutation.isPending || html.trim() === '' || isOverByteLimit
            }
          >
            {saveMutation.isPending ? 'Saving…' : isEditing ? 'Save' : 'Create'}
          </FbButton>
        </div>
      </div>
    </div>
  )
}
